class IPSGoalFinder extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[data-ips-finder-tab]'));
    this.panels = Array.from(this.querySelectorAll('[data-ips-finder-panel]'));

    if (!this.tabs.length || !this.panels.length) return;

    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.activate(index));
      tab.addEventListener('keydown', (event) => this.handleKeydown(event, index));
    });

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:block:select', (event) => {
        const tabIndex = this.tabs.findIndex((tab) => tab.getAttribute('data-shopify-editor-block') === event.detail.blockId);
        if (tabIndex >= 0) this.activate(tabIndex);
      });
    }
  }

  activate(index, focus = false) {
    this.tabs.forEach((tab, tabIndex) => {
      const selected = tabIndex === index;
      tab.classList.toggle('is-active', selected);
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
      if (selected && focus) tab.focus();
    });

    this.panels.forEach((panel, panelIndex) => {
      const selected = panelIndex === index;
      panel.hidden = !selected;
      panel.classList.toggle('is-active', selected);
    });
  }

  handleKeydown(event, index) {
    const lastIndex = this.tabs.length - 1;
    let nextIndex = index;

    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') nextIndex = index === lastIndex ? 0 : index + 1;
    if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') nextIndex = index === 0 ? lastIndex : index - 1;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = lastIndex;
    if (nextIndex === index) return;

    event.preventDefault();
    this.activate(nextIndex, true);
  }
}

if (!customElements.get('ips-goal-finder')) {
  customElements.define('ips-goal-finder', IPSGoalFinder);
}

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-ips-scroll]');
  if (!button) return;

  const target = document.getElementById(button.dataset.ipsScrollTarget);
  if (!target) return;

  const direction = button.dataset.ipsScroll === 'previous' ? -1 : 1;
  const amount = Math.max(target.clientWidth * 0.82, 320);
  target.scrollBy({ left: amount * direction, behavior: 'smooth' });
});


const getAppliedDiscountCodes = (root = document) =>
  [...new Set(Array.from(root.querySelectorAll('[data-ips-applied-code]'))
    .map((element) => element.dataset.ipsAppliedCode?.trim().toUpperCase())
    .filter(Boolean))];

// Confirm codes from Shopify, never from automatic discount titles. Arqum 2026-10-21.
const getCartAppliedDiscountCodes = (cart) => {
  if (Array.isArray(cart?.discount_codes)) {
    return new Set(cart.discount_codes
      .filter((discount) => discount?.applicable === true && discount.code)
      .map((discount) => discount.code.trim().toUpperCase()));
  }

  const codes = new Set();
  const addApplication = (discount) => {
    if (discount?.type === 'discount_code' && discount.title) codes.add(discount.title.trim().toUpperCase());
  };

  cart?.cart_level_discount_applications?.forEach(addApplication);

  cart?.items?.forEach((item) => {
    item?.line_level_discount_allocations?.forEach((allocation) => {
      addApplication(allocation?.discount_application);
    });
  });

  const parser = new DOMParser();
  Object.values(cart?.sections || {}).filter(Boolean).forEach((html) => {
    getAppliedDiscountCodes(parser.parseFromString(html, 'text/html')).forEach((code) => codes.add(code));
  });
  return codes;
};

const getCartPageSectionIds = () => {
  const itemsSectionId = document
    .querySelector('cart-items')
    ?.closest('.shopify-section')
    ?.id?.replace('shopify-section-', '');
  const footerSectionId = document.querySelector('#main-cart-footer')?.dataset.id;

  return [...new Set([itemsSectionId, footerSectionId].filter(Boolean))];
};

// Validate every visible fragment before replacing any cart content. Arqum 2026-10-21.
const getCartDiscountRenderPlan = (cart) => {
  const parser = new DOMParser();
  const cartPageSectionIds = getCartPageSectionIds();
  const isCartPage = cartPageSectionIds.length > 0;
  const replacements = [];
  const addReplacement = (element, html, selector) => {
    const nextElement = html && parser.parseFromString(html, 'text/html').querySelector(selector);
    if (!element || !nextElement) throw new Error('We could not refresh your cart. Reload the page to see the latest total.');
    replacements.push({ element, html: nextElement.innerHTML });
  };

  cartPageSectionIds.forEach((sectionId) => {
    const id = `shopify-section-${sectionId}`;
    addReplacement(document.getElementById(id), cart?.sections?.[sectionId], `#${id}`);
  });

  const currentIcon = document.getElementById('cart-icon-bubble');
  if (currentIcon) {
    addReplacement(currentIcon, cart?.sections?.['cart-icon-bubble'], '.shopify-section');
  }

  const drawer = document.querySelector('cart-drawer');
  if (drawer) addReplacement(drawer.querySelector('#CartDrawer'), cart?.sections?.['cart-drawer'], '#CartDrawer');
  if (!isCartPage && !drawer) throw new Error('We could not find your cart. Reload the page and try again.');
  return { replacements, drawer, isCartPage };
};

// Recover section failures with a read request, without applying the code again. Arqum 2026-10-21.
const prepareCartDiscountUpdate = async (cart) => {
  try {
    getCartDiscountRenderPlan(cart);
    return cart;
  } catch {
    const root = window.Shopify?.routes?.root || '/';
    const sections = ['cart-drawer', 'cart-icon-bubble', ...getCartPageSectionIds()];
    const response = await fetch(`${root}cart?sections=${encodeURIComponent(sections.join(','))}`, { cache: 'no-store' });
    if (!response.ok) throw new Error('We could not refresh your cart. Reload the page to see the latest total.');
    const refreshed = { ...cart, sections: { ...cart.sections, ...await response.json() } };
    getCartDiscountRenderPlan(refreshed);
    return refreshed;
  }
};

// Re-read Shopify when a failed restoration leaves the saved discount uncertain. Arqum 2026-10-21.
const readCurrentCartDiscountState = async () => {
  const root = window.Shopify?.routes?.root || '/';
  const response = await fetch(`${root}cart.js`, { cache: 'no-store' });
  if (!response.ok) throw new Error('Unable to confirm the current cart.');
  const cart = await response.json();
  if (!Array.isArray(cart?.items) || !Number.isFinite(cart.total_price) || !Number.isFinite(cart.item_count)) {
    throw new Error('Unable to confirm the current cart.');
  }
  return prepareCartDiscountUpdate({ ...cart, sections: {} });
};

// Do not offer checkout against totals that Shopify could not confirm. Arqum 2026-10-21.
const showCartDiscountTotalsUnconfirmed = (origin, value) => {
  const message = 'Your discount and total could not be confirmed. Reload the page before checkout.';
  document.querySelectorAll('cart-drawer [data-ips-cart-summary], #main-cart-footer [data-ips-cart-summary]')
    .forEach((summary) => { summary.textContent = message; });
  document.querySelectorAll('cart-drawer .cart-item__price-wrapper, cart-drawer .discounts, cart-drawer .ips-cart-progress, cart-items .cart-item__price-wrapper, cart-items .cart-item__discounted-prices, cart-items .discounts, cart-items .ips-cart-progress, .cart__dynamic-checkout-buttons')
    .forEach((element) => {
      element.hidden = true;
      element.style.setProperty('display', 'none', 'important');
    });
  document.querySelectorAll('cart-drawer [data-ips-cart-feedback], #main-cart-footer [data-ips-cart-feedback]')
    .forEach((status) => {
      status.textContent = '';
      status.hidden = true;
    });
  document.querySelectorAll('cart-drawer [name="checkout"], #main-cart-footer [name="checkout"]')
    .forEach((button) => {
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
    });

  const root = origin === 'drawer' ? document.querySelector('cart-drawer') : document.querySelector('#main-cart-footer');
  const error = root?.querySelector('[data-ips-discount-error]');
  const input = root?.querySelector('input[name="discount"]');
  if (error) {
    error.textContent = message;
    error.hidden = false;
  }
  if (input) {
    input.value = value;
    input.setAttribute('aria-invalid', 'true');
    if (origin !== 'drawer' || root.classList.contains('active')) input.focus();
  }
};

const renderCartDiscountUpdate = (cart, feedback = {}) => {
  const { replacements, drawer, isCartPage } = getCartDiscountRenderPlan(cart);
  replacements.forEach(({ element, html }) => { element.innerHTML = html; });
  if (drawer) {
    drawer.classList.toggle('is-empty', cart.item_count === 0);
    drawer.querySelector('#CartDrawer-Overlay')?.addEventListener('click', drawer.close.bind(drawer));
  }

  // Notify listeners after the shared cart sections have refreshed. Arqum 2026-10-21.
  if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: cart }).catch(() => {});
  }
  document.dispatchEvent(new CustomEvent('ips:cart-updated', { detail: cart }));

  const origin = feedback.origin || (isCartPage ? 'page' : 'drawer');
  const feedbackRoot = origin === 'drawer' ? drawer : document.querySelector('#main-cart-footer');
  const panel = feedbackRoot?.querySelector('[data-ips-discount-panel]');
  const input = panel?.querySelector('input[name="discount"]');
  const error = panel?.querySelector('[data-ips-discount-error]');
  const status = feedbackRoot?.querySelector('[data-ips-cart-feedback]');
  const focusElement = feedback.error ? input : panel?.querySelector('button[type="submit"]');

  if (feedback.error) {
    if (input) {
      input.value = feedback.value || '';
      input.setAttribute('aria-invalid', 'true');
    }
    if (error) {
      error.textContent = feedback.error;
      error.hidden = false;
    }
  } else if (status && feedback.message) {
    status.textContent = feedback.message;
    status.hidden = false;
    window.setTimeout(() => {
      if (status.isConnected) status.hidden = true;
    }, 4200);
  }

  // Refresh the focus trap around the new controls without reopening a closed drawer. Arqum 2026-10-21.
  if (drawer?.classList.contains('active') && typeof trapFocus === 'function') {
    const container = drawer.querySelector('.drawer__inner') || drawer;
    trapFocus(container, (origin === 'drawer' && focusElement) || drawer.querySelector('.drawer__close') || container);
  } else if (origin === 'page') {
    focusElement?.focus();
  }
  return true;
};

const updateCartDiscounts = async (codes, options = {}) => {
  if (options.signal?.aborted) throw Object.assign(new Error('Your offer check was cancelled. Please retry.'), { name:'AbortError' });
  const root = window.Shopify?.routes?.root || '/';
  const controller = new AbortController();
  let timer, cancel;
  // A missing response must release the writer and leave checkout unconfirmed. Arqum 2026-10-28.
  try {
    return await Promise.race([(async () => {
      const response = await fetch(root + 'cart/update.js', {
        method: 'POST',
        signal:controller.signal,
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          discount: codes.join(','),
          sections: ['cart-drawer', 'cart-icon-bubble', ...getCartPageSectionIds()],
          sections_url: window.location.pathname + window.location.search,
        }),
      });
      const cart = await response.json();
      if (controller.signal.aborted) throw Object.assign(new Error('The discount response timed out. Please recheck your cart.'), { name:'AbortError' });
      if (!response.ok) throw new Error(cart?.description || cart?.message || 'Unable to update the discount.');
      return prepareCartDiscountUpdate(cart);
    })(), new Promise((resolve, reject) => {
      cancel = () => { controller.abort(); reject(Object.assign(new Error('The discount response timed out. Please recheck your cart.'), { name:'AbortError' })); };
      timer = window.setTimeout(cancel, options.timeoutMs === 3000 ? 3000 : 10000);
      if (options.signal?.aborted) cancel(); else options.signal?.addEventListener('abort', cancel, { once:true });
    })]);
  } finally { window.clearTimeout(timer); options.signal?.removeEventListener('abort', cancel); }
};

// Wait for the deferred offer runtime only on a verified requested product route. Arqum 2026-10-28.
const requestedPaydayOffer = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.getAll('ips_payday').length !== 1) return null;
    const key = params.get('ips_payday');
    const element = document.getElementById('ips-payday-config');
    const config = element && JSON.parse(element.textContent);
    const offer = config?.offers && Object.prototype.hasOwnProperty.call(config.offers, key) ? config.offers[key] : null;
    if (!offer || !config.product || String(config.product.id) !== offer.productId || !Array.isArray(config.product.variants) || !config.product.variants.some(variant => String(variant.id) === offer.variantId) || !Number.isFinite(Date.parse(config.endExclusive)) || Date.now() >= Date.parse(config.endExclusive)) return null;
    return { key, offer, deadline:Date.parse(config.endExclusive) };
  } catch { return null; }
};
let paydayDeclinedPage = null;
let paydayQuantityChoicePage = null;
const paydayPage = () => window.location.origin + window.location.pathname + window.location.search;
const paydayDeclinedHere = () => paydayDeclinedPage === paydayPage();
const publishPaydayBootstrapState = status => {
  if (typeof window.IPSPayday?.getState === 'function') return;
  const product = document.querySelector('product-info[id^="MainProduct-"]');
  const price = product?.querySelector('[id^="price-"]');
  if (!product || !price) return;
  const unresolved = ['preparing', 'failed'].includes(status);
  product.dataset.ipsPaydayState = status;
  product.toggleAttribute('data-payday-preparing', unresolved);
  product.querySelectorAll('[data-payday-preparation]').forEach(node => node.remove());
  price.hidden = unresolved;
  if (unresolved) {
    const label = document.createElement('p');
    label.dataset.paydayPreparation = '';
    label.className = 'ips-payday-saving';
    label.setAttribute('role', 'status');
    label.textContent = status === 'failed' ? 'Your PAYDAY offer is not confirmed. Reload or retry to check your offer.' : 'Confirming your PAYDAY offer…';
    price.after(label);
  }
  document.dispatchEvent(new CustomEvent('ips:payday-state-change', { detail:{status} }));
};
const prepareDeferredPayday = async (form, { signal } = {}) => {
  if (!requestedPaydayOffer() || paydayDeclinedHere()) return { ready:true, status:'ordinary' };
  const href = window.location.href;
  const controller = new AbortController();
  const abandon = () => controller.abort();
  let timer, stop;
  try {
    if (signal?.aborted) abandon(); else signal?.addEventListener('abort', abandon, { once:true });
    timer = window.setTimeout(() => { publishPaydayBootstrapState(paydayDeclinedHere() ? 'ordinary' : 'failed'); abandon(); }, 6000);
    return await Promise.race([(async () => {
      const runtime = await new Promise(resolve => {
        const inspect = () => {
          const current = window.IPSPayday;
          if (typeof current?.awaitCheckout === 'function' && (!form || typeof current.prepareProductForm === 'function')) { cleanup(); resolve(current); }
        };
        const cancelled = () => { cleanup(); resolve(null); };
        const cleanup = () => { document.removeEventListener('ips:payday-ready', inspect); document.removeEventListener('ips:payday-declined', cancelled); controller.signal.removeEventListener('abort', cancelled); };
        document.addEventListener('ips:payday-ready', inspect);
        document.addEventListener('ips:payday-declined', cancelled);
        controller.signal.addEventListener('abort', cancelled, { once:true });
        if (controller.signal.aborted) cancelled(); else inspect();
      });
      if (paydayDeclinedHere() && !controller.signal.aborted && window.location.href === href && document.visibilityState === 'visible') return { ready:true, status:'ordinary' };
      if (!runtime || controller.signal.aborted || window.location.href !== href || document.visibilityState !== 'visible') return { ready:false, status:'offer_runtime_unavailable' };
      return form ? runtime.prepareProductForm(form, { signal:controller.signal }) : runtime.awaitCheckout({ signal:controller.signal });
    })(), new Promise(resolve => {
      stop = () => resolve({ ready:false, status:'offer_preparation_deadline' });
      if (controller.signal.aborted) stop(); else controller.signal.addEventListener('abort', stop, { once:true });
    })]);
  } catch { return { ready:false, status:'offer_preparation_failed' }; }
  finally { window.clearTimeout(timer); signal?.removeEventListener('abort', abandon); controller.signal.removeEventListener('abort', stop); }
};
window.IPSOfferReadiness = Object.freeze({
  requiresCheckout: () => Boolean(requestedPaydayOffer() && !paydayDeclinedHere()),
  ownsProductForm: form => Boolean(requestedPaydayOffer() && !paydayDeclinedHere() && form?.closest('product-info') === document.querySelector('product-info[id^="MainProduct-"]')),
  prepareCheckout: options => prepareDeferredPayday(null, options),
  prepareProductForm: (form, options) => window.IPSOfferReadiness.ownsProductForm(form) ? prepareDeferredPayday(form, options) : Promise.resolve({ ready:true, status:'ordinary' }),
  isDeclined: paydayDeclinedHere,
  hasQuantityChoice: () => paydayQuantityChoicePage === paydayPage(),
  decline: () => { paydayDeclinedPage = paydayPage(); publishPaydayBootstrapState('ordinary'); document.dispatchEvent(new CustomEvent('ips:payday-declined')); }
});
// Preserve quantity intent that occurs before the deferred offer script loads. Arqum 2026-10-28.
const recordBootstrapQuantity = event => {
  if (typeof window.IPSPayday?.getState === 'function' || !requestedPaydayOffer()) return;
  const target = event.type === 'click' ? event.target.closest('quantity-input button[name="plus"], quantity-input button[name="minus"]') : event.isTrusted && event.target.matches('product-info input[name="quantity"]') ? event.target : null;
  if (target?.closest('product-info') === document.querySelector('product-info[id^="MainProduct-"]')) paydayQuantityChoicePage = paydayPage();
};
document.addEventListener('click', recordBootstrapQuantity, { capture:true });
document.addEventListener('input', recordBootstrapQuantity, { capture:true });
document.addEventListener('change', recordBootstrapQuantity, { capture:true });
const bootstrapPaydayRequest = requestedPaydayOffer();
if (bootstrapPaydayRequest && !paydayDeclinedHere()) {
  publishPaydayBootstrapState('preparing');
  const timer = window.setTimeout(() => { if (!paydayDeclinedHere() && requestedPaydayOffer()) publishPaydayBootstrapState('failed'); }, 6000);
  document.addEventListener('ips:payday-ready', () => { if (typeof window.IPSPayday?.getState === 'function') window.clearTimeout(timer); }, { once:true });
  window.setTimeout(() => publishPaydayBootstrapState('expired'), Math.min(2147483647, bootstrapPaydayRequest.deadline - Date.now()));
}

let cartNavigationPending = false;
  // Confirm the advertised offer before a normal same-window cart navigation. Arqum 2026-10-28.
  document.addEventListener('click', async event => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || !window.IPSOfferReadiness.requiresCheckout() || ['ready', 'ordinary', 'expired'].includes(window.IPSPayday?.getState?.()?.status)) return;
    const anchor = event.target.closest('a[href]');
    if (!anchor || anchor.id === 'cart-icon-bubble' || anchor.hasAttribute('download') || anchor.getAttribute('role') === 'button' || anchor.getAttribute('aria-haspopup') === 'dialog' || anchor.target && anchor.target.toLowerCase() !== '_self') return;
    let destination;
    try { destination = new URL(anchor.href, location.href); } catch { return; }
    if (destination.origin !== location.origin || destination.pathname !== new URL((window.Shopify?.routes?.root || '/') + 'cart', location.href).pathname) return;
    event.preventDefault();
    if (cartNavigationPending) return;
    cartNavigationPending = true;
    const href = location.href;
    const priorBusy = anchor.getAttribute('aria-busy');
    anchor.setAttribute('aria-busy', 'true');
    const label = anchor.textContent;
    try {
      const outcome = await window.IPSOfferReadiness.prepareCheckout();
      if (!outcome.ready) {
        anchor.textContent = outcome.status === 'choice' ? 'Choose your offer, then open cart again' : 'Offer not confirmed. Select here to retry';
        return;
      }
      if (document.visibilityState !== 'visible' || location.href !== href || !anchor.isConnected || new URL(anchor.href, location.href).href !== destination.href) return;
      anchor.textContent = label;
      window.location.assign(destination.href);
    } catch {
      anchor.textContent = 'Offer not confirmed. Select here to retry';
    } finally {
      cartNavigationPending = false;
      if (anchor.isConnected) {
        if (priorBusy === null) anchor.removeAttribute('aria-busy'); else anchor.setAttribute('aria-busy', priorBusy);
      }
    }
  });

let cartDiscountUpdatePending = false;

// Join current-cart capture without holding native checkout beyond three seconds. Arqum 2026-10-28.
let cartCheckoutContextStatus = 'idle';
const prepareCartCheckoutContext = async cart => {
  const runtime = window.IPSConfirmedMetaContext;
  if (typeof runtime?.prepareCheckout !== 'function' && (typeof runtime?.start !== 'function' || typeof runtime?.refresh !== 'function')) { cartCheckoutContextStatus = 'api_unavailable'; return; }
  const deadline = Date.now() + 3000;
  cartCheckoutContextStatus = 'preparing';
  const within = async (promise) => {
    const remaining = deadline - Date.now();
    if (remaining <= 0) return { status: 'checkout_deadline' };
    let timer;
    try {
      return await Promise.race([promise, new Promise(resolve => { timer = window.setTimeout(() => resolve({ status: 'checkout_deadline' }), remaining); })]);
    } finally { window.clearTimeout(timer); }
  };
  try {
    if (typeof runtime.prepareCheckout === 'function') {
      const prepared = await within(runtime.prepareCheckout(cart));
      cartCheckoutContextStatus = prepared?.status === 'checkout_deadline' ? 'deadline' : ['context_write_acknowledged', 'context_snapshot_confirmed', 'unchanged'].includes(prepared?.status) ? 'context_ready' : 'context_unavailable';
      return;
    }
    const started = await within(runtime.start());
    if (started?.status === 'checkout_deadline' || Date.now() >= deadline) { cartCheckoutContextStatus = 'deadline'; return; }
    const refreshed = await within(runtime.refresh());
    cartCheckoutContextStatus = refreshed?.status === 'checkout_deadline' ? 'deadline' : ['context_write_acknowledged', 'context_snapshot_confirmed', 'unchanged'].includes(refreshed?.status) ? 'context_ready' : 'context_unavailable';
  } catch { cartCheckoutContextStatus = 'context_failed'; }
};

const preparedCartCheckoutForms = new WeakSet();
let cartCheckoutUpdatePending = false;

// Forward the current code list, including an explicit removal, into native checkout. Arqum 2026-10-23.
document.addEventListener('submit', async (event) => {
  const form = event.target;
  const button = event.submitter;
  if (!form.matches('#cart, #CartDrawer-Form') || button?.name !== 'checkout') return;
  if (preparedCartCheckoutForms.has(form)) {
    preparedCartCheckoutForms.delete(form);
    return;
  }
  event.preventDefault();

  const scope = form.id === 'CartDrawer-Form' ? document.querySelector('cart-drawer') : document.querySelector('#main-cart-footer');
  const feedback = scope?.querySelector('[data-ips-cart-feedback]');
  const report = (message) => {
    const currentScope = form.id === 'CartDrawer-Form' ? document.querySelector('cart-drawer') : document.querySelector('#main-cart-footer');
    const currentFeedback = currentScope?.querySelector('[data-ips-cart-feedback]');
    if (currentFeedback?.isConnected) { currentFeedback.textContent = message; currentFeedback.hidden = false; }
  };
  const cartViews = Array.from(document.querySelectorAll('cart-items, cart-drawer-items'));
  if (cartDiscountUpdatePending || cartCheckoutUpdatePending || cartViews.some((view) => view.quantityUpdatePending)) {
    report('Your cart is updating. Please wait a moment, then check out.');
    return;
  }

  if (feedback?.isConnected) { feedback.textContent = ''; feedback.hidden = true; }
  cartCheckoutUpdatePending = true;
  let ownsDiscountGuard = false;
  const viewStates = cartViews.map((view) => ({ view, pending: view.quantityUpdatePending }));
  viewStates.forEach(({ view }) => { view.quantityUpdatePending = true; });
  const controls = Array.from(document.querySelectorAll('cart-items button, cart-items input, cart-drawer button, cart-drawer input, #main-cart-footer button, #main-cart-footer input'))
    .map((control) => ({ control, disabled: control.disabled }));
  controls.forEach(({ control }) => { control.disabled = true; });
  const priorBusy = button.getAttribute('aria-busy');
  button.setAttribute('aria-busy', 'true');
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 10000);
  let released = false;
  let checkoutAbandoned = false;
  const checkoutHref = window.location?.href;
  const onCheckoutPageHide = () => { checkoutAbandoned = true; };
  window.addEventListener?.('pagehide', onCheckoutPageHide, { once: true });
  const release = () => {
    if (released) return;
    released = true;
    controls.forEach(({ control, disabled }) => { if (control.isConnected) control.disabled = disabled; });
    viewStates.forEach(({ view, pending }) => { view.quantityUpdatePending = pending; });
    if (priorBusy === null) button.removeAttribute('aria-busy');
    else button.setAttribute('aria-busy', priorBusy);
    if (ownsDiscountGuard) cartDiscountUpdatePending = false;
    cartCheckoutUpdatePending = false;
  };

  try {
    // Resolve an advertised arrival before reading the final native code list. Arqum 2026-10-28.
    if (window.IPSOfferReadiness?.requiresCheckout() || typeof window.IPSPayday?.awaitCheckout === 'function') {
      const offer = window.IPSOfferReadiness?.requiresCheckout() ? await window.IPSOfferReadiness.prepareCheckout({ signal:controller.signal }) : await window.IPSPayday.awaitCheckout({ signal:controller.signal });
      if (!offer?.ready) throw new Error(offer?.status === 'choice' ? 'Choose which offer to keep, then check out again.' : 'Your PAYDAY offer is not confirmed. Please retry checkout to check it again.');
    }
    if (cartDiscountUpdatePending) throw new Error('Your cart is updating. Please wait a moment, then check out.');
    cartDiscountUpdatePending = true;
    ownsDiscountGuard = true;
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart.js`, { cache: 'no-store', signal: controller.signal });
    if (!response.ok) throw new Error('We could not confirm your current offer. Please try checkout again.');
    const cart = await response.json();
    if (controller.signal.aborted) throw Object.assign(new Error('Checkout check timed out.'), { name: 'AbortError' });
    if (!Array.isArray(cart?.items) || !cart.items.length || !Number.isFinite(cart.total_price) ||
        !Array.isArray(cart.discount_codes) || cart.discount_codes.some((discount) => typeof discount?.code !== 'string' || !discount.code.trim())) {
      throw new Error('We could not confirm your current offer. Reload your cart before checkout.');
    }
    if (window.IPSPayday?.validateCheckoutCart && !window.IPSPayday.validateCheckoutCart(cart)) {
      throw new Error('Your PAYDAY offer changed. Reload your cart to confirm the discount before checkout.');
    }
    if (!form.isConnected || !button.isConnected || button.form !== form) {
      throw new Error('Your cart changed. Please check the current total, then check out again.');
    }
    let discount = form.querySelector('[data-ips-checkout-discount]');
    if (!discount) {
      discount = document.createElement('input');
      discount.type = 'hidden';
      discount.name = 'discount';
      discount.dataset.ipsCheckoutDiscount = '';
      form.append(discount);
    }
    discount.value = [...new Set(cart.discount_codes.map((entry) => entry.code.trim()))].join(',');
    window.clearTimeout(timeout);
    await prepareCartCheckoutContext(cart);
    if (checkoutAbandoned || window.location?.href !== checkoutHref || !form.isConnected || !button.isConnected || button.form !== form) {
      throw new Error('Your cart changed. Please check the current total, then check out again.');
    }
    release();
    preparedCartCheckoutForms.add(form);
    if (controller.signal.aborted) throw Object.assign(new Error('Checkout check timed out.'), { name: 'AbortError' });
    form.requestSubmit(button);
  } catch (error) {
    report(error.name === 'AbortError' ? 'Your checkout check timed out. Please try again.' : error.message || 'We could not confirm your current offer. Please try checkout again.');
  } finally {
    window.clearTimeout(timeout);
    window.removeEventListener?.('pagehide', onCheckoutPageHide);
    preparedCartCheckoutForms.delete(form);
    release();
  }
});

document.addEventListener('submit', async (event) => {
  const form = event.target.closest('[data-ips-discount-form]');
  if (!form) return;

  // Keep PAYDAY and other entered offers mutually exclusive. Arqum 2026-10-23.
  if (window.IPSPayday?.handlesForm(form)) { event.preventDefault(); window.IPSPayday.submitForm(form); return; }

  event.preventDefault();
  if (cartDiscountUpdatePending) return;
  const input = form.querySelector('input[name="discount"]');
  const error = form.closest('[data-ips-discount-panel]')?.querySelector('[data-ips-discount-error]');
  const button = form.querySelector('button[type="submit"]');
  const code = input?.value.trim().toUpperCase();
  const origin = form.closest('cart-drawer') ? 'drawer' : 'page';

  if (!code) {
    if (error) {
      error.textContent = 'Enter a discount code.';
      error.hidden = false;
    }
    input?.setAttribute('aria-invalid', 'true');
    input?.focus();
    return;
  }
  window.IPSOfferReadiness?.decline();

  if (error) error.hidden = true;
  input?.removeAttribute('aria-invalid');
  cartDiscountUpdatePending = true;
  button?.setAttribute('aria-busy', 'true');
  if (button) button.disabled = true;

  try {
    const existingCodes = getAppliedDiscountCodes(document);
    const codes = [...new Set([...existingCodes, code])];
    let cart = await updateCartDiscounts(codes);
    const appliedCodes = getCartAppliedDiscountCodes(cart);
    const applied = appliedCodes.has(code);

    // A rejected new code must not silently remove the previous valid voucher. Arqum 2026-10-21.
    if (!applied && existingCodes.some((existingCode) => !appliedCodes.has(existingCode))) {
      try {
        cart = await updateCartDiscounts(existingCodes);
      } catch {
        try {
          cart = await readCurrentCartDiscountState();
          renderCartDiscountUpdate(cart, {
            origin,
            error: 'That code could not be applied. Your cart is refreshed below; please recheck the current discount and total before checkout.',
            value: code,
          });
        } catch {
          showCartDiscountTotalsUnconfirmed(origin, code);
        }
        return;
      }
    }

    renderCartDiscountUpdate(
      cart,
      applied
        ? { origin, message: `${code} applied. Your total has been updated.` }
        : { origin, error: 'That code is not valid for the items in this cart.', value: code },
    );
  } catch (requestError) {
    if (error) {
      error.textContent = requestError.message || 'We could not apply that code. Please try again.';
      error.hidden = false;
    }
    input?.setAttribute('aria-invalid', 'true');
    const drawer = form.closest('cart-drawer');
    if (!drawer || drawer.classList.contains('active')) input?.focus();
  } finally {
    cartDiscountUpdatePending = false;
    if (button) button.disabled = false;
    button?.removeAttribute('aria-busy');
  }
});

document.addEventListener('click', async (event) => {
  const removeButton = event.target.closest('[data-ips-remove-discount]');
  if (!removeButton) return;

  event.preventDefault();
  if (cartDiscountUpdatePending) return;
  const code = removeButton.dataset.ipsRemoveDiscount;
  if (!code) return;
  window.IPSOfferReadiness?.decline();
  const drawer = removeButton.closest('cart-drawer');
  const origin = drawer ? 'drawer' : 'page';
  const discountScope = drawer || document;
  const remainingCodes = getAppliedDiscountCodes(discountScope).filter(
    (appliedCode) => appliedCode.toUpperCase() !== code.toUpperCase(),
  );

  cartDiscountUpdatePending = true;
  removeButton.disabled = true;
  removeButton.setAttribute('aria-busy', 'true');

  try {
    const cart = await updateCartDiscounts(remainingCodes);
    renderCartDiscountUpdate(cart, getCartAppliedDiscountCodes(cart).has(code.toUpperCase())
      ? { origin, error: 'That discount could not be removed. Please try again.' }
      : { origin, message: `${code} removed. Your total has been updated.` });
  } catch (requestError) {
    const status = (drawer || document.querySelector('#main-cart-footer'))?.querySelector('[data-ips-cart-feedback]');
    if (status) {
      status.textContent = requestError.message || 'We could not remove that code. Please try again.';
      status.hidden = false;
    }
  } finally {
    cartDiscountUpdatePending = false;
    removeButton.disabled = false;
    removeButton.removeAttribute('aria-busy');
  }
});

(() => {
  const isCartPage = window.location.pathname === '/cart';
  if (isCartPage) return;

  const setupHomepageCartToast = () => {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || drawer.dataset.ipsHomepageToastReady === 'true') return;

    drawer.dataset.ipsHomepageToastReady = 'true';

    const toast = document.createElement('div');
    toast.className = 'ips-cart-toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    toast.setAttribute('aria-atomic', 'true');
    toast.innerHTML = `
      <span class="ips-cart-toast__icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" focusable="false"><path d="M6.5 12.5 10 16l7.5-8"/></svg>
      </span>
      <span class="ips-cart-toast__copy">
        <strong>Added to your cart</strong>
        <span data-ips-cart-toast-product>Product added successfully</span>
      </span>
      <a class="ips-cart-toast__link" href="/cart">View cart</a>
    `;
    // Keep confirmation out of the accessibility tree until an add succeeds. Arqum 2026-10-05.
    toast.hidden = true;
    document.body.appendChild(toast);

    const productName = toast.querySelector('[data-ips-cart-toast-product]');
    let dismissTimer;

    const showToast = (state) => {
      const name = state?.product_title || state?.title || 'Product added successfully';
      productName.textContent = name === 'Default Title' ? 'Product added successfully' : name;

      window.clearTimeout(dismissTimer);
      toast.hidden = false;
      toast.classList.remove('is-visible');
      toast.getBoundingClientRect();
      toast.classList.add('is-visible');
      dismissTimer = window.setTimeout(() => {
        toast.classList.remove('is-visible');
        toast.hidden = true;
      }, 3600);
    };

    // Confirm only after the shared drawer and cart count refresh successfully. Arqum 2026-10-05.
    drawer.renderContents = async function renderHomepageCartContents(parsedState) {
      await this.refreshContents(parsedState);
      showToast(parsedState);
    };
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupHomepageCartToast, { once: true });
  } else {
    setupHomepageCartToast();
  }

  document.addEventListener('shopify:section:load', setupHomepageCartToast);
})();

/* IPS header motion enhancement - v1.8 */
(() => {
  const setupScrollHeader = () => {
    const header = document.querySelector('.section-header');
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)');

    if (!header || header.dataset.ipsScrollHeaderReady === 'true') return;

    header.dataset.ipsScrollHeaderReady = 'true';
    header.dataset.ipsScrollHeader = '';

    if (window.Shopify?.designMode || reduceMotion?.matches) return;

    let lastScrollY = window.scrollY;
    let downDistance = 0;
    let upDistance = 0;
    let ticking = false;

    const showHeader = () => header.classList.remove('ips-header--hidden');
    const updateHeader = () => {
      const currentScrollY = window.scrollY;
      const distance = currentScrollY - lastScrollY;

      if (currentScrollY < 96) {
        downDistance = 0;
        upDistance = 0;
        showHeader();
      } else if (distance > 0) {
        downDistance += distance;
        upDistance = 0;

        if (downDistance >= 36) header.classList.add('ips-header--hidden');
      } else if (distance < 0) {
        upDistance += Math.abs(distance);
        downDistance = 0;

        if (upDistance >= 12) showHeader();
      }

      lastScrollY = currentScrollY;
      ticking = false;
    };

    window.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        window.requestAnimationFrame(updateHeader);
        ticking = true;
      },
      { passive: true },
    );

    header.addEventListener('focusin', showHeader);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupScrollHeader, { once: true });
  } else {
    setupScrollHeader();
  }

  document.addEventListener('shopify:section:load', setupScrollHeader);
})();


/* Update the IPS cart summary from the same successful cart response. */
document.addEventListener('ips:cart-updated', (event) => {
  const footer = document.getElementById('main-cart-footer');
  const sectionId = footer?.dataset.id;
  const sectionMarkup = event.detail?.sections?.[sectionId];
  const currentSummary = footer?.querySelector('.ips-cart-bottom');

  if (!sectionMarkup || !currentSummary) return;

  const nextDocument = new DOMParser().parseFromString(sectionMarkup, 'text/html');
  const nextSummary = nextDocument.querySelector('.ips-cart-bottom');

  if (nextSummary) currentSummary.replaceWith(nextSummary);
});


/* Update the free-delivery monitor from the same successful cart response. */
document.addEventListener('ips:cart-updated', (event) => {
  const cartItems = document.querySelector('cart-items');
  const sectionId = cartItems?.querySelector('#main-cart-items')?.dataset.id;
  const sectionMarkup = event.detail?.sections?.[sectionId];
  const currentProgress = cartItems?.querySelector('.ips-cart-progress');

  // Clear stale progress when the last item is removed. Arqum 2026-10-04.
  if (event.detail?.item_count === 0) {
    currentProgress?.remove();
    return;
  }

  if (!sectionMarkup || !currentProgress) return;

  const nextDocument = new DOMParser().parseFromString(sectionMarkup, 'text/html');
  const nextProgress = nextDocument.querySelector('.ips-cart-progress');

  if (nextProgress) currentProgress.replaceWith(nextProgress);
});
